import React, { useState, useMemo } from 'react';
import styles from './FullTextBulkUploader.module.css';
import {
  BulkPDFUploader,
  ImportResults,
  ReferenceManagerHelper,
  ReferenceManagerFileExporter,
  XMLUploader,
  SinglePDFUploaderSection,
} from './components';
import { exportAndDownload } from './components/exportAndDownload';
import { createImportJob } from './components/XMLUploader/query';
import { ImportJob, importJobSuccessful } from './index';
import FullTextImportJobHeader from 'components/FullTextImportJobHeader/FullTextImportJobHeader';
import { IconList, Link, Message, Text } from 'components/core';
import { Stack } from 'components/layout';

type Props = {
  reviewId: string;
  lastProcessedImport: ImportJob | null;
  onCompletePath: string;
};

const FullTextBulkUploader = ({
  reviewId,
  lastProcessedImport,
  onCompletePath,
}: Props) => {
  const [fileExporterError, setFileExporterError] = useState('');
  const [importJob, setImportJob] = useState(lastProcessedImport);
  const importSuccess = useMemo(() => importJobSuccessful(importJob), [
    importJob,
  ]);
  const onSuccessfulPDFUpload = (updatedImportJob: ImportJob): void => {
    setImportJob(updatedImportJob);
  };

  return (
    <>
      <div className={styles.FullTextBulkUploader}>
        <div className={styles.bottomSpace}>
          <Message
            type="primary"
            variant="prominent"
            icon={IconList.solid.faCircleInfo}
            dismissible={false}
          >
            <Text>
              Use your reference manager (e.g. EndNote, Zotero) to bulk upload
              full text.{' '}
              <Link
                to="https://support.covidence.org/help/bulk-pdf-import"
                external={true}
                variant="primary"
              >
                Learn more about bulk upload
              </Link>
            </Text>
          </Message>
        </div>
        <Stack spacing={3} component="ol" className={styles.StepsList}>
          <li>
            <ReferenceManagerFileExporter
              downloadStudies={exportAndDownload(reviewId)}
              setErrorMessage={setFileExporterError}
            />
            {fileExporterError && (
              <div className={styles.errorMessage}>{fileExporterError}</div>
            )}
          </li>
          <li>
            <ReferenceManagerHelper />
          </li>
          <li>
            <XMLUploader
              createImportJob={createImportJob}
              reviewId={reviewId}
              importJob={importJob}
              uploadedFiles={importJob?.files_uploaded || 0}
              onCreateImportJob={() => {
                setImportJob(null);
              }}
              onFinishImportJob={(job) => {
                if (job) setImportJob(job);
              }}
            />
          </li>
        </Stack>
        <div aria-live="assertive">
          {importSuccess && importJob && importJob.files_uploaded === 0 && (
            <div className={styles.bottomSpace}>
              <BulkPDFUploader
                reviewId={reviewId}
                importJob={importJob}
                onProcessed={onSuccessfulPDFUpload}
              />
            </div>
          )}
        </div>
      </div>

      <div role="region" aria-live="assertive">
        {importJob && importJob.files_uploaded > 0 && (
          <>
            <div className={styles.FullTextBulkUploader__jobHeader}>
              <FullTextImportJobHeader
                fileName={importJob.file_name}
                uploadedWhen={importJob.uploaded_when}
                uploadedBy={importJob.uploaded_by}
              />
            </div>
            <div className={styles.FullTextBulkUploader__results}>
              <ImportResults
                uploadedFiles={importJob.files_uploaded}
                totalFiles={importJob.found_with_full_text}
                onCompletePath={onCompletePath}
              />
              {importJob && importJob.expected_files.length > 0 && (
                <>
                  <BulkPDFUploader
                    reviewId={reviewId}
                    importJob={importJob}
                    onProcessed={onSuccessfulPDFUpload}
                    text={
                      'Drag and drop the PDFs still to upload here or upload them individually'
                    }
                  />
                  <SinglePDFUploaderSection
                    reviewId={reviewId}
                    importJob={importJob}
                    onProcessed={onSuccessfulPDFUpload}
                  />
                </>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default FullTextBulkUploader;
